import { onAuthStateChanged, type User } from 'firebase/auth';
import {
  Activity,
  Building2,
  Gauge,
  Headset,
  Layers,
  LineChart,
  ListChecks,
  LogOut,
  MessagesSquare,
  Moon,
  ScrollText,
  ServerCog,
  Sun,
  UsersRound,
} from 'lucide-react';
import { lazy, Suspense, useEffect, useState } from 'react';
import { Navigate, NavLink, Route, BrowserRouter as Router, Routes, useNavigate } from 'react-router';
import { ApiError, api } from './api/client';
import { AssistCallHost } from './components/AssistCallHost';
import { NavItem, Shell } from './components/Shell';
import type { StaffMe } from './api/types';
import { auth, signOut as googleSignOut } from './auth/firebase';
import { useIdleSignOut } from './auth/idle';
import { endPasswordSession, noteSessionEnded, onPasswordSession, passwordSession, sessionEnded } from './auth/session';
import { IdleWarning, idleLimitLabel } from './components/IdleWarning';
import { config } from './config';
import { AssistCallProvider } from './lib/assistCall';
import { MeContext, can } from './lib/me';
import { applyTheme, storedTheme } from './lib/theme';
import { notifyAssist, notifySupport, SCREEN_REQUEST, useAssistPending } from './lib/useAssistPending';
import { Alerting } from './screens/Alerting';
import { Assist } from './screens/Assist';
import { Audit } from './screens/Audit';
import { Vitals } from './screens/Vitals';
import { Checks } from './screens/Checks';
import { Logs } from './screens/Logs';
import { Metrics } from './screens/Metrics';
import { Now } from './screens/Now';
import { QueueJobsRoute } from './screens/QueueJobs';
import { Queues } from './screens/Queues';
import { SignIn } from './screens/SignIn';
import { Support } from './screens/Support';
import { Systems } from './screens/Systems';
import { Tenants } from './screens/Tenants';
import { Users } from './screens/Users';

/**
 * Every screen, and the permission that opens it (services/staff.js). A tab
 * somebody cannot open is not drawn, and its route does not exist for them --
 * a bookmark to it lands on the first screen they can open instead.
 */
const TABS = [
  { to: '/', label: 'Now', icon: Activity, permission: 'incidents.view' },
  { to: '/checks', label: 'Checks', icon: ListChecks, permission: 'checks.view' },
  { to: '/queues', label: 'Queues', icon: Layers, permission: 'queues.view' },
  { to: '/tenants', label: 'Tenants', icon: Building2, permission: 'tenants.view' },
  { to: '/audit', label: 'Audit', icon: ScrollText, permission: 'audit.view' },
  { to: '/assist', label: 'Assist', icon: Headset, permission: 'assist.view' },
  { to: '/support', label: 'Support', icon: MessagesSquare, permission: 'support.view' },
  { to: '/vitals', label: 'Vitals', icon: LineChart, permission: 'vitals.view' },
  { to: '/metrics', label: 'Metrics', icon: Gauge, permission: 'metrics.view' },
  { to: '/systems', label: 'Systems', icon: ServerCog, permission: 'systems.view' },
  { to: '/users', label: 'Users', icon: UsersRound, permission: 'staff.manage' },
];

export type ScreenTab = (typeof TABS)[number];

/** The fixture harness (screens/PreviewApp.tsx), in dev builds only. */
const PreviewApp = import.meta.env.DEV ? lazy(() => import('./screens/PreviewApp')) : null;

/**
 * Inside the router, so it can navigate on behalf of something that is not: a
 * desktop notification's click (lib/useAssistPending.ts, SCREEN_REQUEST), or a
 * screen's own code that may also run in the router-less fixture preview
 * (components/ScreenLink.tsx, useOpenScreen).
 */
function OpenRequestedScreens() {
  const navigate = useNavigate();
  useEffect(() => {
    const open = (event: Event) => {
      const path = (event as CustomEvent<unknown>).detail;
      if (typeof path === 'string' && path.startsWith('/')) navigate(path);
    };
    window.addEventListener(SCREEN_REQUEST, open);
    return () => window.removeEventListener(SCREEN_REQUEST, open);
  }, [navigate]);
  return null;
}

/**
 * What /me said. `refused` is a 403 -- the server knows who this is and says
 * no, with the reason -- and is told apart from `error`, which is the server
 * not answering, because only the second is worth a Retry button.
 */
type Access =
  | { state: 'loading' }
  | { state: 'ok'; me: StaffMe }
  | { state: 'refused'; message: string }
  | { state: 'error'; message: string };

/**
 * Sign out of whichever door this browser came in by -- both, should both be
 * open. A password session is ended on the server too; a remembered browser is
 * kept, because remembering is about the browser rather than the visit.
 * `reason` is what the sign-in screen says, when it was not the person's own
 * choice.
 */
const signOutEverywhere = (reason: string | null = null) => {
  if (reason) noteSessionEnded(reason);
  if (passwordSession()) void endPasswordSession(reason);
  if (auth.currentUser) void googleSignOut();
};

/** Until /me says otherwise: the server's default (config.signin.idleMinutes). */
const IDLE_MINUTES = 120;

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [passwordSignIn, setPasswordSignIn] = useState(passwordSession);
  const [theme, setTheme] = useState(storedTheme());
  const [access, setAccess] = useState<Access>({ state: 'loading' });
  const [accessTick, setAccessTick] = useState(0);
  const [idleMinutes, setIdleMinutes] = useState(IDLE_MINUTES);
  const me = access.state === 'ok' ? access.me : null;

  // Who this is, by either door: a sphere-watch password session, or Google.
  // `key` changes when the person does, and only then.
  useEffect(() => onPasswordSession(() => setPasswordSignIn(passwordSession())), []);
  const identity = passwordSignIn
    ? { email: passwordSignIn.email, key: `password:${passwordSignIn.token}` }
    : user
      ? { email: user.email ?? '', key: `google:${user.uid}` }
      : null;
  const identityKey = identity?.key ?? null;

  // Who this is to sphere-watch, as opposed to who it is to Google. Read once
  // per sign-in: a change somebody makes to your access applies on your next
  // load, and the server enforces it on every request in the meantime.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `accessTick` is the Retry button: bumping it asks /me again
  useEffect(() => {
    // Reset on sign-out too, so the next account never renders a frame with
    // the previous one's permissions.
    setAccess({ state: 'loading' });
    if (!identityKey) return;
    let cancelled = false;
    api
      .get<{ staff: StaffMe; signin?: { idleMinutes?: number } }>('/me')
      .then(({ staff, signin }) => {
        if (cancelled) return;
        setAccess({ state: 'ok', me: staff });
        if (signin?.idleMinutes && signin.idleMinutes > 0) setIdleMinutes(signin.idleMinutes);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const message = error instanceof Error ? error.message : String(error);
        setAccess(
          error instanceof ApiError && error.status === 403
            ? { state: 'refused', message }
            : { state: 'error', message },
        );
      });
    return () => {
      cancelled = true;
    };
  }, [identityKey, accessTick]);

  // Only once /me has answered, and only the queues this person can open: the
  // poll is an authenticated call, and firing it on the sign-in screen -- or
  // for a screen they cannot see -- would just log a refusal every fifteen
  // seconds.
  const {
    pending: assistPending,
    lastArrival,
    supportPending,
    supportUrgent,
    lastSupportArrival,
    incidents,
  } = useAssistPending({
    assist: can(me, 'assist.view'),
    support: can(me, 'support.view'),
    incidents: can(me, 'incidents.view'),
  });

  // A desktop notification per ARRIVAL, keyed off the rising edge the hook
  // reports rather than off the count -- otherwise every poll that still finds
  // somebody waiting fires again, and the alert becomes noise within a minute.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the count is read, not depended on: a queue shrinking is not a new arrival (see above)
  useEffect(() => {
    if (lastArrival && assistPending > 0) notifyAssist(assistPending);
    // assistPending is read, not depended on: re-running when the count changes
    // without a new arrival (someone was accepted) would announce a shrinking
    // queue as if it were a new request.
  }, [lastArrival]);

  // The same rule for help conversations, under their own notification tag.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the counts are read, not depended on: a queue shrinking is not a new arrival (see above)
  useEffect(() => {
    if (lastSupportArrival && supportPending > 0) notifySupport(supportPending, supportUrgent);
  }, [lastSupportArrival]);

  // Signed out after idleMinutes with nobody at the console, a minute's
  // warning first (auth/idle.ts). Not known until Firebase has said whether a
  // Google sign-in was kept: a click before then must not freshen a stale one.
  const idle = useIdleSignOut(ready ? Boolean(identity) : null, idleMinutes * 60_000, () =>
    signOutEverywhere(`You were signed out after ${idleLimitLabel(idleMinutes)} without use.`),
  );
  const idleWarning =
    idle.leftMs !== null ? (
      <IdleWarning
        leftMs={idle.leftMs}
        limitMinutes={idleMinutes}
        onStay={idle.stay}
        onSignOut={() => signOutEverywhere()}
      />
    ) : null;

  useEffect(
    () =>
      onAuthStateChanged(auth, (next) => {
        setUser(next);
        setReady(true);
      }),
    [],
  );

  if (!ready) return <div className="wt-empty">…</div>;

  // The fixture board renders without a backend or a signed-in identity, so a
  // layout regression is caught here rather than on the screen someone is
  // reading at 3am. Dev only -- it is not in the production bundle's routes.
  const themeButton = (
    <button
      type="button"
      className="btn btn-icon"
      title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
      onClick={() => {
        const next = theme === 'dark' ? 'light' : 'dark';
        setTheme(next);
        applyTheme(next);
      }}
    >
      {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
    </button>
  );

  // The fixture harness, dev only and loaded only when asked for: a static
  // import put every fixture -- names, addresses, a staff email -- into the
  // production bundle, where nothing could reach them but anyone could read
  // them. `import.meta.env.DEV` is false in a build, so this whole branch and
  // the chunk behind it are dropped.
  if (PreviewApp && window.location.pathname === '/preview') {
    return (
      <Suspense fallback={<div className="wt-empty">…</div>}>
        <PreviewApp tabs={TABS} tools={themeButton} />
      </Suspense>
    );
  }

  if (!identity) return <SignIn notice={sessionEnded()} />;

  if (access.state === 'loading') return <div className="wt-empty">…</div>;

  if (access.state !== 'ok') {
    return (
      <div className="wt-signin">
        <div className="card">
          <h1 style={{ fontSize: 18, marginTop: 0 }}>sphere-watch</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            Signed in as <strong>{identity.email}</strong>.
          </p>
          <p className="wt-signin-error" role="alert">
            {access.state === 'refused' ? access.message : `Could not reach sphere-watch: ${access.message}`}
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            {access.state === 'error' && (
              <button type="button" className="btn btn-secondary" onClick={() => setAccessTick((n) => n + 1)}>
                Retry
              </button>
            )}
            <button type="button" className="btn btn-primary" onClick={() => signOutEverywhere()}>
              Use a different account
            </button>
          </div>
        </div>
        {idleWarning}
      </div>
    );
  }

  const allowed = TABS.filter((tab) => can(me, tab.permission));
  const home = allowed[0]?.to;

  return (
    <MeContext.Provider value={me}>
      <Router>
        <OpenRequestedScreens />
        <AssistCallProvider>
          <Shell
            env={{ label: config.env, kind: config.env }}
            waiting={assistPending + supportPending}
            who={
              <>
                <span className="wt-side-email" title={identity.email}>
                  {identity.email}
                </span>
                {me?.isRoot && <span className="badge badge-accent">root</span>}
              </>
            }
            tools={
              <>
                {themeButton}
                <button
                  type="button"
                  className="btn btn-icon"
                  title={`Sign out ${identity.email}`.trim()}
                  onClick={() => signOutEverywhere()}
                >
                  <LogOut size={15} />
                </button>
              </>
            }
            nav={allowed.map((tab) => (
              <NavLink key={tab.to} to={tab.to} end={tab.to === '/'} title={tab.label}>
                {/* Counts only where somebody is needed: people waiting on the
                    two queues, and incidents nobody has acknowledged on Now. */}
                <NavItem
                  icon={tab.icon}
                  label={tab.label}
                  count={
                    tab.to === '/assist'
                      ? assistPending
                      : tab.to === '/support'
                        ? supportPending
                        : tab.to === '/'
                          ? incidents.unacknowledged
                          : 0
                  }
                  countTitle={
                    tab.to === '/support' && supportUrgent
                      ? `${supportUrgent} urgent`
                      : tab.to === '/'
                        ? `${incidents.unacknowledged} not acknowledged · ${incidents.open} open`
                        : undefined
                  }
                />
              </NavLink>
            ))}
          >
            {/* The screen-share call, drawn above whichever screen is open so
                leaving Assist does not end it (lib/assistCall.tsx). */}
            <AssistCallHost />
            <Routes>
              {can(me, 'incidents.view') ? (
                <Route path="/" element={<Now />} />
              ) : (
                <Route
                  path="/"
                  element={
                    home ? (
                      <Navigate to={home} replace />
                    ) : (
                      <div className="wt-empty">
                        Your account can sign in but has no screens yet. Ask an admin to give you some on the Users
                        screen.
                      </div>
                    )
                  }
                />
              )}
              {can(me, 'checks.view') && <Route path="/checks" element={<Checks />} />}
              {can(me, 'queues.view') && <Route path="/queues" element={<Queues />} />}
              {can(me, 'queues.view') && <Route path="/queues/:system/:queue" element={<QueueJobsRoute />} />}
              {can(me, 'tenants.view') && <Route path="/tenants" element={<Tenants />} />}
              {can(me, 'audit.view') && <Route path="/audit" element={<Audit />} />}
              {can(me, 'assist.view') && <Route path="/assist" element={<Assist />} />}
              {can(me, 'support.view') && <Route path="/support" element={<Support />} />}
              {can(me, 'vitals.view') && <Route path="/vitals" element={<Vitals />} />}
              {can(me, 'metrics.view') && <Route path="/metrics" element={<Metrics />} />}
              {can(me, 'systems.view') && <Route path="/systems" element={<Systems />} />}
              {/* Under Systems, not beside it: the log is about the monitor rather
                  than anything monitored, and nesting keeps the Systems tab lit. */}
              {can(me, 'systems.view') && <Route path="/systems/logs" element={<Logs />} />}
              {can(me, 'systems.view') && <Route path="/systems/alerting" element={<Alerting />} />}
              {can(me, 'staff.manage') && <Route path="/users" element={<Users />} />}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Shell>
        </AssistCallProvider>
        {idleWarning}
      </Router>
    </MeContext.Provider>
  );
}
